import { supabase, withRetry } from './supabase';
import { taskLogger } from './taskLogger';
import { checkUserHasEnoughCredits, chargeUserCredits } from './credits';
import { debugMetaToken, exchangeForLongLivedToken, fetchPageAccessToken } from './metaTokens';

interface FacebookPostPayload {
  message: string;
  access_token: string;
  url?: string;
  requireImage?: boolean;
}

export async function validateFacebookAccessToken(accessToken: string): Promise<boolean> {
  if (!accessToken) {
    return false;
  }

  try {
    const response = await fetch(
      `https://graph.facebook.com/v21.0/me?access_token=${accessToken}`,
      {
        cache: 'no-cache',
        headers: {
          'Accept': 'application/json'
        }
      }
    );
    
    return response.ok;
  } catch (error) {
    return false;
  }
}

export async function refreshPageAccessToken(accountId: string): Promise<string | null> {
  if (!accountId) {
    taskLogger.error('refreshPageAccessToken: accountId is required');
    return null;
  }

  try {
    const { data: account, error } = await supabase
      .from('connected_accounts')
      .select('access_token, account_type, platform')
      .eq('id', accountId)
      .single();

    if (error || !account?.access_token) {
      taskLogger.error('Failed to get account', { accountId, error });
      return null;
    }

    let newToken: string | null = null;

    if (account.account_type === 'page') {
      // debug_token i pobranie tokena strony wymagaja tokena aplikacji (appId|appSecret),
      // wiec robi to funkcja brzegowa - sekret nie moze byc w bundlu.
      let validateData: Awaited<ReturnType<typeof debugMetaToken>>;
      try {
        validateData = await debugMetaToken(account.access_token);
      } catch (error) {
        taskLogger.error('Failed to validate page token', { accountId, error: error instanceof Error ? error.message : error });
        return null;
      }

      if (validateData?.is_valid && validateData?.expires_at === 0) {
        taskLogger.info('Page token is already long-lived', { accountId });
        return account.access_token;
      }

      try {
        newToken = await fetchPageAccessToken(accountId);
      } catch (error) {
        taskLogger.error('Failed to refresh page token', { accountId, error: error instanceof Error ? error.message : error });
        return null;
      }

    } else if (account.account_type === 'profile' || account.account_type === 'group') {
      try {
        newToken = await exchangeForLongLivedToken(account.access_token);
      } catch (error) {
        taskLogger.error('Failed to exchange token', { accountId, accountType: account.account_type, error: error instanceof Error ? error.message : error });
        return null;
      }
    } else {
      taskLogger.error('Unknown account type', { accountId, accountType: account.account_type });
      return null;
    }

    if (!newToken) {
      taskLogger.error('No new token received', { accountId });
      return null;
    }

    const tokenExpiresAt = new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString();
    const { error: updateError } = await supabase
      .from('connected_accounts')
      .update({ access_token: newToken, token_expires_at: tokenExpiresAt })
      .eq('id', accountId);

    if (updateError) {
      taskLogger.error('Failed to update token in database', { accountId, error: updateError });
      return null;
    }

    taskLogger.info('Token refreshed successfully', { accountId, accountType: account.account_type });
    return newToken;
  } catch (error) {
    taskLogger.error('refreshPageAccessToken error', { accountId, error: error instanceof Error ? error.message : 'Unknown error' });
    return null;
  }
}

export async function refreshInstagramAccessToken(accountId: string): Promise<string | null> {
  if (!accountId) {
    taskLogger.error('refreshInstagramAccessToken: accountId is required');
    return null;
  }

  try {
    const { data: account, error } = await supabase
      .from('connected_accounts')
      .select('access_token, platform')
      .eq('id', accountId)
      .single();

    if (error || !account?.access_token) {
      taskLogger.error('Failed to get Instagram account', { accountId, error });
      return null;
    }

    let newToken: string | null = null;
    try {
      newToken = await exchangeForLongLivedToken(account.access_token, 'instagram');
    } catch (error) {
      taskLogger.error('Failed to refresh Instagram token', { accountId, error: error instanceof Error ? error.message : error });
      return null;
    }

    if (!newToken) {
      taskLogger.error('No new token received for Instagram', { accountId });
      return null;
    }

    const tokenExpiresAt = new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString();
    const { error: updateError } = await supabase
      .from('connected_accounts')
      .update({ access_token: newToken, token_expires_at: tokenExpiresAt })
      .eq('id', accountId);

    if (updateError) {
      taskLogger.error('Failed to update Instagram token in database', { accountId, error: updateError });
      return null;
    }

    taskLogger.info('Instagram token refreshed successfully', { accountId });
    return newToken;
  } catch (error) {
    taskLogger.error('refreshInstagramAccessToken error', { accountId, error: error instanceof Error ? error.message : 'Unknown error' });
    return null;
  }
}

export async function getPageAccessToken(pageId: string): Promise<string> {
  if (!pageId) {
    throw new Error('Page ID is required');
  }

  try {
    const { data, error } = await supabase
      .from('connected_accounts')
      .select('access_token')
      .eq('id', pageId)
      .eq('platform', 'facebook')
      .single();

    if (error || !data?.access_token) {
      throw new Error('No valid Facebook account found');
    }

    // Validate token
    const isValid = await validateFacebookAccessToken(data.access_token);
    if (!isValid) {
      // Try to refresh token
      const newToken = await refreshPageAccessToken(pageId);
      if (!newToken) {
        throw new Error('Failed to refresh Facebook access token');
      }
      return newToken;
    }

    return data.access_token;
  } catch (error) {
    throw error instanceof Error 
      ? error 
      : new Error('Failed to get Facebook access token');
  }
}

async function uploadImageToFacebook(imageUrl: string, accessToken: string, pageId: string): Promise<string | null> {
  try {
    taskLogger.info('Starting image upload to Facebook', {
      pageId,
      imageUrl: imageUrl.substring(0, 100)
    });

    // Fetch the image
    const response = await fetch(imageUrl, {
      method: 'GET',
      headers: {
        'Accept': 'image/*'
      }
    });

    if (!response.ok) {
      taskLogger.error('Failed to fetch image', {
        pageId,
        imageUrl,
        status: response.status
      });
      return null;
    }

    const blob = await response.blob();
    
    // Create form data for upload
    const formData = new FormData();
    formData.append('source', blob);
    formData.append('published', 'false');
    formData.append('access_token', accessToken);

    // Upload to Facebook
    const uploadResponse = await fetch(
      `https://graph.facebook.com/v21.0/${pageId}/photos`,
      {
        method: 'POST',
        body: formData
      }
    );

    if (!uploadResponse.ok) {
      const errorData = await uploadResponse.json();
      throw new Error(errorData.error?.message || `Upload failed: ${uploadResponse.statusText}`);
    }

    const uploadResult = await uploadResponse.json();
    
    taskLogger.info('Image upload successful', {
      pageId,
      photoId: uploadResult.id
    });

    return uploadResult.id;
  } catch (error) {
    taskLogger.error('Image upload error', {
      pageId,
      error: error instanceof Error ? error.message : 'Unknown error',
      imageUrl: imageUrl.substring(0, 100)
    });
    return null;
  }
}

export async function publishToFacebook(pageId: string, payload: FacebookPostPayload): Promise<any> {
  let attempts = 0;
  const maxAttempts = 3;
  const baseDelay = 2000;
  const maxDelay = 10000;

  while (attempts < maxAttempts) {
    try {
      taskLogger.info('Starting Facebook publish process', { pageId });
      
      if (!pageId || !payload.access_token) {
        throw new Error('Missing required Facebook parameters');
      }

      // Get task ID from metadata
      const { data: tasks } = await supabase
        .from('scheduled_tasks')
        .select('id, webhook_url')
        .eq('account_id', pageId)
        .eq('status', 'active')
        .limit(1);

      if (!tasks?.length) {
        throw new Error('Task not found');
      }

      const task = tasks[0];

      // Check and charge credits before publishing
      const hasEnoughCredits = await checkUserHasEnoughCredits(task.id);
      if (!hasEnoughCredits) {
        throw new Error('Insufficient credits to publish post');
      }

      // Validate and refresh token
      const isValid = await validateFacebookAccessToken(payload.access_token);
      if (!isValid) {
        throw new Error('Invalid Facebook access token');
      }

      // Get the ready post to check for webhook-modified image
      const { data: readyPosts } = await supabase
        .from('posts')
        .select('*')
        .eq('task_id', task.id)
        .eq('status', 'ready')
        .order('created_at', { ascending: false })
        .limit(1);

      const readyPost = readyPosts?.[0];
      const webhookResponse = readyPost?.metadata?.webhook_response?.data;
      
      // Handle image upload
      let photoId = null;
      if (payload.url || webhookResponse?.image_url) {
        // Try webhook-modified image first
        if (webhookResponse?.image_url) {
          photoId = await uploadImageToFacebook(webhookResponse.image_url, payload.access_token, pageId);
          taskLogger.info('Tried uploading webhook-modified image', {
            success: !!photoId,
            taskId: task.id
          });
        }

        // If webhook image failed or wasn't available, try original image
        if (!photoId && payload.url && payload.url !== webhookResponse?.image_url) {
          photoId = await uploadImageToFacebook(payload.url, payload.access_token, pageId);
          taskLogger.info('Tried uploading original image', {
            success: !!photoId,
            taskId: task.id
          });
        }

        if (!photoId && payload.requireImage) {
          throw new Error('Failed to upload required image');
        }
      }

      // Create post data
      const postData = new URLSearchParams();
      postData.append('message', payload.message);
      postData.append('access_token', payload.access_token);
      
      if (photoId) {
        postData.append('attached_media[0][media_fbid]', photoId);
      }

      const postResponse = await fetch(
        `https://graph.facebook.com/v21.0/${pageId}/feed`,
        {
          method: 'POST',
          body: postData
        }
      );

      if (!postResponse.ok) {
        const errorData = await postResponse.json();
        throw new Error(errorData.error?.message || `Facebook API error: ${postResponse.statusText}`);
      }

      const postResult = await postResponse.json();
      if (!postResult.id) {
        throw new Error('Invalid response from Facebook API: missing post ID');
      }

      // Verify post
      const verifyResponse = await fetch(
        `https://graph.facebook.com/v21.0/${postResult.id}?fields=id,permalink_url,created_time&access_token=${payload.access_token}`
      );

      if (!verifyResponse.ok) {
        throw new Error('Failed to verify post creation');
      }

      const verifyData = await verifyResponse.json();
      if (!verifyData.id || !verifyData.permalink_url) {
        throw new Error('Post verification failed - missing required fields');
      }

      // Charge credits after successful publish
      const creditsCharged = await chargeUserCredits(task.id);
      if (!creditsCharged) {
        throw new Error('Failed to charge credits for post');
      }

      taskLogger.info('Post published successfully', {
        pageId,
        postId: verifyData.id,
        hasImage: !!photoId,
        creditsCharged: true,
        imageSource: webhookResponse?.image_url ? 'webhook' : 'original'
      });

      return {
        platform_post_id: verifyData.id,
        platform_post_url: verifyData.permalink_url,
        platform_post_created_at: verifyData.created_time || new Date().toISOString(),
        status: 'published'
      };

    } catch (error) {
      taskLogger.error('Publish attempt failed', {
        pageId,
        attempt: attempts + 1,
        error: error instanceof Error ? error.message : 'Unknown error'
      });

      attempts++;
      if (attempts < maxAttempts) {
        const delay = Math.min(baseDelay * Math.pow(2, attempts), maxDelay);
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }
      throw error;
    }
  }

  throw new Error('Failed to publish to Facebook after retries');
}